import React, { useCallback, useEffect, useState } from 'react';

import { Map } from '@strava/feature-global-wasm-mre';

import { useMRE } from '../../hooks/useMRE';
import type { PrivacyZone } from '../../types';

interface PrivacyZonesProps {
  privacyZones: PrivacyZone[];
}

export const PrivacyZones = ({ privacyZones }: PrivacyZonesProps) => {
  const { terrainEngine } = useMRE();

  const [scaledPrivacyZones, setScaledPrivacyZones] = useState<PrivacyZone[]>(
    []
  );

  // Update the scaled privacy zones based on the current camera scale
  // This ensures that the privacy zones maintain a consistent size on screen
  // regardless of zoom level.
  const updateScaledPrivacyZones = useCallback(() => {
    if (!terrainEngine) {
      return;
    }

    // Get the current scale in meters per pixel from the terrain engine
    const metersPerPixel = terrainEngine.getCamera().getScaleMetersPerPixel();

    // Avoid division by zero or invalid values
    if (!metersPerPixel || metersPerPixel <= 0) {
      return;
    }

    setScaledPrivacyZones(
      privacyZones.map((zone) => ({
        ...zone,
        radius: zone.radius / metersPerPixel
      }))
    );
  }, [terrainEngine, privacyZones]);

  // Add a view update listener to the terrain engine to update the scaled privacy zones when the view is updated
  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    // Create a listener
    const viewUpdateListener = {
      onViewUpdated: updateScaledPrivacyZones
    };

    // Add the listener to the terrain engine
    terrainEngine?.addViewUpdateListener(viewUpdateListener);

    return () => {
      // Clean up the listener when the component unmounts
      terrainEngine?.removeViewUpdateListener(viewUpdateListener);
    };
  }, [updateScaledPrivacyZones, terrainEngine]);

  // if there are no scaled privacy zones, do not render anything
  if (!scaledPrivacyZones || scaledPrivacyZones.length === 0) {
    return null;
  }

  return (
    <>
      {scaledPrivacyZones.map((zone, index) => {
        const { position, radius } = zone;
        const { lat, lng } = position;

        if (
          typeof lat === 'undefined' || // lat can be 0 (falsy)
          typeof lng === 'undefined' || // lng can be 0 (falsy)
          radius <= 0 // radius must be positive
        ) {
          return null;
        }

        return (
          <div data-testid="privacy-zone" key={`privacy-zone-${index}`}>
            <Map.DrawCircleOnMap2D latLng={[lat, lng]} radius={radius} />
          </div>
        );
      })}
    </>
  );
};

export default PrivacyZones;
